import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useSession } from '@/lib/auth/session-context';
import { loginErrorKey } from './error-message';
import { SESSION_KEY } from './session-query';

type Field = 'username' | 'password' | 'totp';
type FieldErrors = Partial<Record<Field, 'required' | 'format'>>;

const TOTP_PATTERN = /^\d{6}$/;

function validate(values: Record<Field, string>): FieldErrors {
  const errors: FieldErrors = {};
  if (values.username.trim() === '') errors.username = 'required';
  if (values.password === '') errors.password = 'required';
  if (!TOTP_PATTERN.test(values.totp)) errors.totp = 'format';
  return errors;
}

export interface LoginPageProps {
  /** Called with the signed-in user's destination once sign-in succeeds. */
  onSignedIn: () => void;
}

/**
 * PRD VS-001 sign-in. Keyboard-first: the first field is focused on arrival,
 * Enter submits from any field, and on failure focus moves to the first
 * invalid field or to the announced error.
 */
export function LoginPage({ onSignedIn }: LoginPageProps) {
  const { t } = useTranslation(['auth', 'common']);
  const session = useSession();
  const queryClient = useQueryClient();
  const ids = { form: useId(), totpHint: useId(), error: useId() };

  const [values, setValues] = useState<Record<Field, string>>({
    username: '',
    password: '',
    totp: '',
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const refs = {
    username: useRef<HTMLInputElement>(null),
    password: useRef<HTMLInputElement>(null),
    totp: useRef<HTMLInputElement>(null),
  };
  const errorRef = useRef<HTMLDivElement>(null);

  const login = useMutation({
    mutationFn: () =>
      session.login({
        username: values.username.trim(),
        password: values.password,
        totp: values.totp,
      }),
    onSuccess: (user) => {
      queryClient.setQueryData(SESSION_KEY, user);
      onSignedIn();
    },
    onError: () => {
      // A code is single-use and may have been consumed; ask for a fresh one.
      setValues((current) => ({ ...current, totp: '' }));
    },
  });

  useEffect(() => {
    refs.username.current?.focus();
  }, [refs.username]);

  useEffect(() => {
    if (login.isError) errorRef.current?.focus();
  }, [login.isError]);

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (login.isPending) return;
    const errors = validate(values);
    setFieldErrors(errors);
    const firstInvalid = (['username', 'password', 'totp'] as const).find((f) => errors[f]);
    if (firstInvalid) {
      refs[firstInvalid].current?.focus();
      return;
    }
    login.mutate();
  }

  function update(field: Field, value: string): void {
    setValues((current) => ({ ...current, [field]: value }));
    if (fieldErrors[field]) setFieldErrors((current) => ({ ...current, [field]: undefined }));
  }

  const fieldError = (field: Field) => {
    const kind = fieldErrors[field];
    if (!kind) return null;
    const key =
      field === 'username'
        ? 'auth:validation.usernameRequired'
        : field === 'password'
          ? 'auth:validation.passwordRequired'
          : 'auth:validation.totpFormat';
    return (
      <p id={`${ids.form}-${field}-error`} className="text-sm text-destructive">
        {t(key)}
      </p>
    );
  };

  const describedBy = (field: Field, extra?: string) =>
    [fieldErrors[field] ? `${ids.form}-${field}-error` : null, extra].filter(Boolean).join(' ') ||
    undefined;

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <p className="text-sm font-semibold tracking-widest text-primary">
            {t('common:app.name')}
          </p>
          <CardTitle>{t('auth:login.title')}</CardTitle>
          <CardDescription>{t('auth:login.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            id={ids.form}
            className="space-y-4"
            noValidate
            onSubmit={onSubmit}
            aria-busy={login.isPending}
          >
            {login.isError && (
              <Alert id={ids.error} ref={errorRef} role="alert" variant="destructive" tabIndex={-1}>
                {t(loginErrorKey(login.error))}
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor={`${ids.form}-username`}>{t('auth:login.username')}</Label>
              <Input
                id={`${ids.form}-username`}
                ref={refs.username}
                name="username"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                value={values.username}
                onChange={(e) => update('username', e.target.value)}
                aria-invalid={fieldErrors.username ? true : undefined}
                aria-describedby={describedBy('username')}
                disabled={login.isPending}
              />
              {fieldError('username')}
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${ids.form}-password`}>{t('auth:login.password')}</Label>
              <Input
                id={`${ids.form}-password`}
                ref={refs.password}
                name="password"
                type="password"
                autoComplete="current-password"
                value={values.password}
                onChange={(e) => update('password', e.target.value)}
                aria-invalid={fieldErrors.password ? true : undefined}
                aria-describedby={describedBy('password')}
                disabled={login.isPending}
              />
              {fieldError('password')}
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${ids.form}-totp`}>{t('auth:login.totp')}</Label>
              <Input
                id={`${ids.form}-totp`}
                ref={refs.totp}
                name="totp"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                className="font-mono tracking-[0.4em]"
                value={values.totp}
                onChange={(e) => update('totp', e.target.value.replace(/\D/g, ''))}
                aria-invalid={fieldErrors.totp ? true : undefined}
                aria-describedby={describedBy('totp', ids.totpHint)}
                disabled={login.isPending}
              />
              <p id={ids.totpHint} className="text-sm text-muted-foreground">
                {t('auth:login.totpHint')}
              </p>
              {fieldError('totp')}
            </div>

            <Button type="submit" className="w-full" disabled={login.isPending}>
              {login.isPending ? (
                <>
                  <Spinner />
                  {t('auth:login.submitting')}
                </>
              ) : (
                t('auth:login.submit')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
